import React, { useState, useEffect } from 'react';
import { 
  Row, 
  Col, 
  Card, 
  Button, 
  Table, 
  Tag, 
  Form, 
  Input, 
  Select, 
  DatePicker, 
  Alert, 
  Space,
  Statistic,
  Tooltip,
  Modal,
  message,
  ConfigProvider
} from 'antd';
import { 
  FilterOutlined
} from '@ant-design/icons';
import { Expense, ExpenseFilters, ExpenseStats } from '../../types/expense';
import { expenseService } from '../../services/expenseService';
import ExpenseModal from '../../components/expenses/ExpenseModal';
import { formatCurrency, formatDate } from '../../utils/formatters';
import dayjs from 'dayjs';
import styles from './ExpensesPage.module.css';
import locale from 'antd/locale/vi_VN';

const { RangePicker } = DatePicker;
const { Option } = Select;

const ExpensesPage: React.FC = () => {
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [stats, setStats] = useState<ExpenseStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState<Expense | undefined>();
  const [error, setError] = useState('');
  const [filters, setFilters] = useState<ExpenseFilters>({
    date_from: dayjs().startOf('month').format('YYYY-MM-DD'),
    date_to: dayjs().endOf('month').format('YYYY-MM-DD')
  });

  useEffect(() => {
    loadExpenses();
    loadStats();
  }, [filters]);

  const loadExpenses = async () => {
    setLoading(true);
    try {
      const data = await expenseService.getExpenses(filters);
      setExpenses(data);
    } catch (error: any) {
      setError('Lỗi khi tải danh sách chi phí');
      console.error('Lỗi:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const data = await expenseService.getExpenseStats();
      setStats(data);
    } catch (error) {
      console.error('Lỗi khi tải thống kê:', error);
    }
  };

  const handleSave = () => {
    loadExpenses();
    loadStats();
  };

  const handleEdit = (expense: Expense) => {
    setSelectedExpense(expense);
    setShowModal(true);
  };

  const handleDelete = async (expense: Expense) => {
    Modal.confirm({
      title: 'Xác nhận xóa',
      content: `Bạn có chắc chắn muốn xóa chi phí "${expense.title}" (${expense.expense_number})?`,
      okText: 'Xóa',
      okType: 'danger',
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          await expenseService.deleteExpense(expense.id);
          message.success('Xóa chi phí thành công');
          loadExpenses();
          loadStats();
        } catch (error: any) {
          message.error('Lỗi khi xóa chi phí: ' + (error.response?.data?.message || error.message));
        }
      },
    });
  };



  const handleFilterChange = (name: string, value: any) => {
    setFilters(prev => ({
      ...prev,
      [name]: value || undefined
    }));
  };

  const clearFilters = () => {
    setFilters({
      date_from: dayjs().startOf('month').format('YYYY-MM-DD'),
      date_to: dayjs().endOf('month').format('YYYY-MM-DD')
    });
  };

  const getSummaryStats = () => {
    if (!stats) return null;

    return (
      <div className={styles.statsContainer}>
        <Row gutter={[24, 16]} className={styles.statsRow}>
          <Col xs={24} sm={12} lg={6}>
            <Card className={styles.statCard} hoverable>
              <div className={styles.statContent}>
                <div className={styles.statIcon} style={{ background: '#3b82f6' }}>
                  <i className="fas fa-dollar-sign" />
                </div>
                <div className={styles.statInfo}>
                  <Statistic
                    title="Tổng chi phí"
                    value={stats.total_amount || 0}
                    formatter={(value) => formatCurrency(Number(value))}
                    valueStyle={{ color: '#1f2937', fontSize: '24px', fontWeight: 'bold' }}
                  />
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className={styles.statCard} hoverable>
              <div className={styles.statContent}>
                <div className={styles.statIcon} style={{ background: '#f59e0b' }}>
                  <i className="fas fa-clock" />
                </div>
                <div className={styles.statInfo}>
                  <Statistic
                    title="Chờ duyệt"
                    value={stats.total_pending || 0}
                    formatter={(value) => formatCurrency(Number(value))}
                    valueStyle={{ color: '#1f2937', fontSize: '24px', fontWeight: 'bold' }}
                  />
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className={styles.statCard} hoverable>
              <div className={styles.statContent}>
                <div className={styles.statIcon} style={{ background: '#10b981' }}>
                  <i className="fas fa-check-circle" />
                </div>
                <div className={styles.statInfo}>
                  <Statistic
                    title="Đã thanh toán"
                    value={stats.total_paid || 0}
                    formatter={(value) => formatCurrency(Number(value))}
                    valueStyle={{ color: '#1f2937', fontSize: '24px', fontWeight: 'bold' }}
                  />
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className={styles.statCard} hoverable>
              <div className={styles.statContent}>
                <div className={styles.statIcon} style={{ background: '#ef4444' }}>
                  <i className="fas fa-times-circle" />
                </div>
                <div className={styles.statInfo}>
                  <Statistic
                    title="Đã hủy"
                    value={stats.total_cancelled || 0}
                    formatter={(value) => formatCurrency(Number(value))}
                    valueStyle={{ color: '#1f2937', fontSize: '24px', fontWeight: 'bold' }}
                  />
                </div>
              </div>
            </Card>
          </Col>
        </Row>
      </div>
    );
  };

  const getStatusTag = (status: string) => {
    switch (status) {
      case 'paid':
        return <Tag color="success">Đã thanh toán</Tag>;
      case 'pending':
        return <Tag color="warning">Chờ thanh toán</Tag>;
      case 'cancelled':
        return <Tag color="error">Đã hủy</Tag>;
      default:
        return <Tag color="default">Không xác định</Tag>;
    }
  };

  const getPaymentMethodText = (method: string) => {
    switch (method) {
      case 'cash':
        return 'Tiền mặt';
      case 'bank_transfer':
        return 'Chuyển khoản';
      case 'credit_card':
        return 'Thẻ tín dụng';
      case 'other':
        return 'Khác';
      default:
        return method;
    }
  };

  const columns = [
    {
      title: 'Mã',
      dataIndex: 'expense_number',
      key: 'expense_number',
      render: (text: string) => <code>{text}</code>,
    },
    {
      title: 'Tiêu đề',
      dataIndex: 'title',
      key: 'title',
      render: (text: string, record: Expense) => (
        <div>
          <div><strong>{text}</strong></div>
          {record.description && (
            <div style={{ color: '#666', fontSize: '12px' }}>{record.description}</div>
          )}
        </div>
      ),
    },
    {
      title: 'Danh mục',
      dataIndex: 'category_name',
      key: 'category_name',
    },
    {
      title: 'Số tiền',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number) => (
        <span style={{ fontWeight: 'bold', color: '#ff4d4f' }}>
          {formatCurrency(amount)}
        </span>
      ),
    },
    {
      title: 'Ngày',
      dataIndex: 'expense_date',
      key: 'expense_date',
      render: (date: string) => formatDate(date),
    },
    {
      title: 'Phương thức',
      dataIndex: 'payment_method',
      key: 'payment_method',
      render: (method: string) => getPaymentMethodText(method),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'payment_status',
      key: 'payment_status',
      render: (status: string) => getStatusTag(status),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      render: (_: any, record: Expense) => (
        <Space>
          <Button 
            type="link" 
            size="small"
            icon={<i className="fas fa-edit" />}
            onClick={() => handleEdit(record)}
          >
            Sửa
          </Button>
          <Button 
            type="link" 
            size="small"
            danger
            icon={<i className="fas fa-trash" />}
            onClick={() => handleDelete(record)}
          >
            Xóa
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <ConfigProvider locale={locale}>
      <div className={styles.container}>
        {getSummaryStats()}

        <Card className={styles.tableCard}>
          <div className={styles.tableHeader}>
            <div className={styles.filterSection}>
              <Space size="middle" wrap>
                <DatePicker
                  placeholder="Từ ngày"
                  value={filters.date_from ? dayjs(filters.date_from) : undefined}
                  onChange={(date) => handleFilterChange('date_from', date ? dayjs(date).format('YYYY-MM-DD') : undefined)}
                  style={{ minWidth: 150 }}
                />
                <DatePicker
                  placeholder="Đến ngày"
                  value={filters.date_to ? dayjs(filters.date_to) : undefined}
                  onChange={(date) => handleFilterChange('date_to', date ? dayjs(date).format('YYYY-MM-DD') : undefined)}
                  style={{ minWidth: 150 }}
                />
                <Select
                  placeholder="Trạng thái"
                  value={filters.payment_status}
                  onChange={(value) => handleFilterChange('payment_status', value)}
                  allowClear
                  style={{ minWidth: 150 }}
                >
                  <Option value="pending">Chờ thanh toán</Option>
                  <Option value="paid">Đã thanh toán</Option>
                  <Option value="cancelled">Đã hủy</Option>
                </Select>
                {(filters.date_from || filters.date_to || filters.payment_status) && (
                  <Button
                    type="link"
                    icon={<FilterOutlined />}
                    onClick={clearFilters}
                    size="small"
                  >
                    Xóa bộ lọc
                  </Button>
                )}
              </Space>
            </div>
            <div className={styles.actionButtons}>
              <Space size="middle">
                <Tooltip title="Làm mới dữ liệu">
                  <Button
                    icon={<i className="fas fa-sync-alt" />}
                    onClick={() => {
                      loadExpenses();
                      loadStats();
                    }}
                    className={styles.actionButton}
                  >
                    Làm mới
                  </Button>
                </Tooltip>
                <Tooltip title="Thêm chi phí mới">
                  <Button
                    type="primary"
                    icon={<i className="fas fa-plus" />}
                    onClick={() => {
                      setSelectedExpense(undefined);
                      setShowModal(true);
                    }}
                    className={styles.primaryButton}
                  >
                    Thêm chi phí
                  </Button>
                </Tooltip>
              </Space>
            </div>
          </div>

          {error && <Alert message={error} type="error" style={{ marginBottom: '16px' }} />}
          
          <Table
            columns={columns}
            dataSource={expenses}
            rowKey="id"
            loading={loading}
            className={styles.expensesTable}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} chi phí`,
              className: styles.tablePagination,
              pageSizeOptions: ['10', '20', '50', '100'],
            }}
            locale={{
              emptyText: (
                <div style={{ textAlign: 'center', padding: '32px', color: '#666' }}>
                  <p>Không có chi phí nào được tìm thấy</p>
                </div>
              ),
            }}
            size="middle"
            scroll={{ x: 1200 }}
          />
        </Card>

        {/* Modal thêm/sửa chi phí */}
        <ExpenseModal
          visible={showModal}
          onClose={() => setShowModal(false)}
          expense={selectedExpense}
          onSuccess={handleSave}
        />
      </div>
    </ConfigProvider>
  );
};

export default ExpensesPage;
